import { useMemo, useState } from "react";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, SortDirection } from "../types";

export interface TableControls<TSort extends string> {
  sortBy: TSort;
  sortDir: SortDirection;
  page: number;
  pageSize: number;
  setSort: (sortBy: TSort) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
}

interface UseTableControlsArgs<TSort extends string> {
  defaultSortBy: TSort;
  resetKey: string;
  defaultPageSize?: number;
  // When set, the chosen page size is remembered in sessionStorage under this key
  // (e.g. one per tab) so it survives navigating away and back within the session.
  persistKey?: string;
}

const pageSizeStorageKey = (persistKey: string) => `menuAnalytics.pageSize.${persistKey}`;

const readStoredPageSize = (persistKey: string | undefined, fallback: number): number => {
  if (!persistKey) {
    return fallback;
  }
  try {
    const stored = parseInt(sessionStorage.getItem(pageSizeStorageKey(persistKey)) ?? "", 10);
    return PAGE_SIZE_OPTIONS.includes(stored) ? stored : fallback;
  } catch {
    return fallback;
  }
};

export const useTableControls = <TSort extends string>({
  defaultSortBy,
  resetKey,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  persistKey,
}: UseTableControlsArgs<TSort>): TableControls<TSort> => {
  const [sort, setSortState] = useState<{ sortBy: TSort; sortDir: SortDirection }>({
    sortBy: defaultSortBy,
    sortDir: "desc",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => readStoredPageSize(persistKey, defaultPageSize));

  // Reset to page 1 when the shared filters change
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (resetKey !== prevResetKey) {
    setPrevResetKey(resetKey);
    setPage(1);
  }

  const setSort = (nextSortBy: TSort) => {
    setSortState(prev => ({
      sortBy: nextSortBy,
      sortDir: prev.sortBy === nextSortBy && prev.sortDir === "desc" ? "asc" : "desc",
    }));
    setPage(1);
  };

  const setPageSizeAndReset = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(1);
    if (persistKey) {
      try {
        sessionStorage.setItem(pageSizeStorageKey(persistKey), String(nextPageSize));
      } catch {
        // Best-effort persistence; the in-memory pageSize state above already applied.
      }
    }
  };

  return useMemo(
    () => ({ sortBy: sort.sortBy, sortDir: sort.sortDir, page, pageSize, setSort, setPage, setPageSize: setPageSizeAndReset }),
    [sort, page, pageSize]
  );
};
